import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CarouselSlideComponent } from '@commons/controls/select/carousel-slide/carousel-slide.component';
import { carouselSlideAnimation } from '@commons/controls/select/carousel/carousel.animation';
import { EventTrackerInterface } from '@commons/tracker/event-tracker/event-tracker.interface';
import { EventTrackerService } from '@commons/tracker/event-tracker/event-tracker.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  animations: [carouselSlideAnimation],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CarouselComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent implements AfterContentInit, ControlValueAccessor {
  private static COUNT = 0;
  id: string;

  onChange: (_: string) => void;
  onTouched: () => void;
  disabled: boolean;
  model: CarouselSlideComponent;

  @ContentChildren(CarouselSlideComponent) slides: QueryList<CarouselSlideComponent>;

  @Input() ariaLabelledBy: string;
  @Input() moveEvent: EventTrackerInterface;
  @Output() previous = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();

  constructor(private readonly cdr: ChangeDetectorRef, private readonly eventTrackerService: EventTrackerService) {
    CarouselComponent.COUNT++;
    this.id = `carousel-${CarouselComponent.COUNT}`;
  }

  ngAfterContentInit(): void {
    this.slides.changes.pipe(filter((_) => !!this.model)).subscribe((_) => this.writeValue(this.model.value));
  }

  registerOnChange(fn: (_: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  writeValue(value: string): void {
    const existingSlide = this.getSlide(value);

    this.selectSlide(existingSlide);
  }

  getSlide(value: string): CarouselSlideComponent {
    let existingSlide = this.slides.find((s) => s.value === value);

    if (!existingSlide) {
      existingSlide = new CarouselSlideComponent();
      existingSlide.value = value;
    }
    return existingSlide;
  }

  doPrevious(): void {
    if (this.isFirst()) {
      return;
    }

    if (this.moveEvent) {
      this.eventTrackerService.send({
        eventCategory: this.moveEvent.eventCategory,
        eventAction: this.moveEvent.eventAction,
        eventLabel: 'Back',
      });
    }

    const slides = this.slides.toArray();
    const previousIndex = slides.indexOf(this.model) - 1;
    this.previous.emit();
    this.selectSlide(slides[previousIndex]);
  }

  doNext(): void {
    if (this.isLast()) {
      return;
    }
    if (this.moveEvent) {
      this.eventTrackerService.send({
        eventCategory: this.moveEvent.eventCategory,
        eventAction: this.moveEvent.eventAction,
        eventLabel: 'Next',
      });
    }

    const slides = this.slides.toArray();
    const nextIndex = slides.indexOf(this.model) + 1;
    this.next.emit();
    this.selectSlide(slides[nextIndex]);
  }

  isFirst(): boolean {
    return this.model ? this.model === this.slides.first : true;
  }

  isLast(): boolean {
    return this.model ? this.model === this.slides.last : true;
  }

  selectSlide(selected: CarouselSlideComponent): void {
    // reset all slides selected/previous state to false
    this.resetAllSlides();

    // set the new selected slide
    this.setModel(selected);

    if (this.onChange) {
      this.onChange(this.model.value);
    }
    this.cdr.markForCheck();
  }

  resetAllSlides(): void {
    for (const slide of this.slides.toArray()) {
      slide.selected = false;
      slide.previous = false;
    }
  }

  setModel(selected: CarouselSlideComponent): void {
    // if there was a previously selected slide,
    // it is now the previous one (needed to animate the transition between two slides).
    if (this.model) {
      this.model.selected = false;
      this.model.previous = true;
    }

    this.model = selected;
    this.model.selected = true;
    this.model.previous = false;
  }
}
